import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { apiClient } from '../../services/api';
import { hasAuthData } from '../../utils/authHeaders';
import styles from './ReferralWelcomeModal.module.css';

interface ReferrerInfo {
  id: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
}

interface ReferralWelcomeModalProps {
  referrerInfo: ReferrerInfo;
  onClose: () => void;
}

const ReferralWelcomeModal = ({ referrerInfo, onClose }: ReferralWelcomeModalProps) => {
  const { t } = useTranslation('common');
  const { haptic } = useTelegramWebApp();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Загрузка аватара реферера
  useEffect(() => {
    const loadAvatar = async () => {
      if (!hasAuthData()) return;
      
      try {
        const data = await apiClient.user.getReferrerPhoto(referrerInfo.id);
        if (data.success && data.photoUrl) {
          setAvatarUrl(data.photoUrl);
        }
      } catch (error) {
        // Тихо игнорируем ошибки
      }
    };

    loadAvatar();
  }, [referrerInfo.id]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleClose = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic.impact('light');
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  const displayName = referrerInfo.firstName 
    ? `${referrerInfo.firstName}${referrerInfo.lastName ? ' ' + referrerInfo.lastName : ''}`
    : referrerInfo.username || t('loadingScreen.user');
  
  const initials = referrerInfo.firstName?.[0] || referrerInfo.username?.[0] || 'U';

  return createPortal(
    <div 
      className={styles.backdrop} 
      onClick={handleBackdropClick}
      onTouchEnd={handleBackdropClick}
    >
      <div 
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <button 
          className={styles.closeButton} 
          onClick={handleClose}
          onTouchEnd={handleClose}
          title={t('btn.close')}
        >
          ×
        </button>

        <div className={styles.content}>
          <h2 className={styles.title}>{t('referralWelcome.invitedBy')}</h2>

          <div className={styles.avatarContainer}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className={styles.avatar} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                {initials}
              </div>
            )}
          </div>

          <div className={styles.name}>{displayName}</div>
          
          {referrerInfo.username && (
            <div className={styles.username}>@{referrerInfo.username}</div>
          )}

          <div className={styles.divider}></div>

          <div className={styles.instructions}>
            <p className={styles.instructionText}>
              {t('referralWelcome.useApp')}
            </p>
            <p className={styles.bonusText}>
              {t('referralWelcome.bonus')}
            </p>
          </div>

          <button 
            className={styles.actionButton} 
            onClick={handleClose}
            onTouchEnd={handleClose}
          >
            {t('btn.ok')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReferralWelcomeModal;

